import type { PostHog } from 'posthog-js';

// Lazy PostHog init, deferred until after hydration: init at module-eval inserts the
// array.js loader <script> before hydration and collides with SSR'd <script> (e.g. JsonLd)
// → hydration mismatch (posthog-js#1645). Call ensurePostHogInit() at the top of any effect
// or handler touching posthog.
//
// posthog-js itself is DYNAMIC-imported here (~62 KB gz) so the SDK stays out of every
// page's first-load JS — this module only ships the type + the loader shim. All posthog
// access goes through withPostHog(), which runs synchronously once the SDK is up and
// otherwise queues FIFO behind the in-flight load.

let instance: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;

export function ensurePostHogInit(): Promise<PostHog | null> {
  if (initPromise) return initPromise;
  if (typeof window === 'undefined') return Promise.resolve(null);
  const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
  if (!token) return Promise.resolve(null);

  initPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(token, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
        ui_host: 'https://us.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: 'history_change',
        capture_pageleave: true,
        autocapture: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-private]',
          recordCrossOriginIframes: false,
        },
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        debug: process.env.NODE_ENV === 'development',
      });
      instance = posthog;
      return posthog;
    })
    .catch(() => {
      // Chunk fetch can reject (stale hash after a deploy, ad-blocker, flaky
      // network). Resolve null so queued withPostHog callbacks no-op instead of
      // raising unhandledrejection per call, and clear the cache so the next
      // call retries the load.
      initPromise = null;
      return null;
    });
  return initPromise;
}

/**
 * Run `fn` against the PostHog instance — synchronously when already initialized,
 * otherwise after the deferred SDK load completes (callbacks chain FIFO on the same
 * promise, so event order is preserved). No-ops on the server or when PostHog is
 * unconfigured (no token).
 */
export function withPostHog(fn: (ph: PostHog) => void): void {
  // Never throws: analytics calls ride on click handlers (Link onClick, upgrade CTA) and a
  // throw there would cancel the navigation or skip the action after it.
  const run = (ph: PostHog) => {
    try {
      fn(ph);
    } catch (err) {
      console.error('[PostHog] call failed:', err);
    }
  };
  if (instance) {
    run(instance);
    return;
  }
  void ensurePostHogInit().then((ph) => {
    if (ph) run(ph);
  });
}
